import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  .interests {
    margin-top: 30px;
  }

  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  /* The template's offset frame: the accent border sits 14px down and to
     the right, and hover slides the photo toward it. That reads as a
     deliberate frame behind a square.

     The template's duotone (grayscale + multiply over the accent) is still
     off — against terracotta it washes a face in orange. Natural colour is
     warmer, which is the point of having a photo. */
  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
      transition: var(--transition);
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, [prefersReducedMotion]);

  const skills = [
  'Communication',
  'Adaptability',
  'Leadership and teamwork',
  'Risk Management & Insurance',
  'Financial modeling',
  'Process improvement',
];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              I started at{' '}
              <a href="https://www.kirkwood.edu/" target="_blank" rel="noreferrer">
                Kirkwood Community College
              </a>
              , earned an Associate of Arts, and transferred to the{' '}
              <a href="https://uiowa.edu/" target="_blank" rel="noreferrer">
                University of Iowa
              </a>{' '}
              to study Finance and Risk Management &amp; Insurance.
            </p>

            <p>
              My past jobs weren’t related to my degree, but they taught me how to work. Mess
              something up on the line at{' '}
              <a href="https://biggrove.com/" target="_blank" rel="noreferrer">
                Big Grove Brewery
              </a>{' '}
              and it comes back to you within minutes. On a{' '}
              <a href="https://transportation.uiowa.edu/cambus" target="_blank" rel="noreferrer">
                CAMBUS
              </a>{' '}
              route, forty people are assuming the person driving is paying attention. Both punish
              sloppiness fast, and both taught me to stay sharp, keep my work consistent, and hold
              myself to an output I can actually be proud of.
            </p>

            <p>
              I bring that same mindset to my free time. I spend it building projects tied to
              finance and risk management, using them to sharpen the skills my degree is built on
              and to prepare for the next step in my career. I’d rather understand something for
              real than fake my way through it, so I treat being early in the field as a reason to
              stay curious.
            </p>

            <p>A few things I’ll bring with me:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>

          <p className="interests">
            Off the clock I’m usually outside — hiking, fishing, disc golf, basketball,
            pickleball, whatever game is going. And I like going places I haven’t been.
          </p>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Wyatt Olsen"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
